import React, { useState, useEffect, useRef } from 'react'
import { Trans, Button } from '../../atoms'
import { Form, Formik, FieldArray } from 'formik'
import FormField from '../../atoms/FormField/FormField'
import FormInput from '../../atoms/FormInput/FormInput'
import { api } from '../../../utils'
import { useRouter } from 'next/router'
import Alert from '../../atoms/Alert/Alert'
import { AlertTriangleLine, Info } from '../../atoms/NewIcons'
import useTrans from '../../../hooks/useTrans'
import * as Sentry from '@sentry/browser'

api.defaults.xsrfCookieName = 'csrftoken'
api.defaults.xsrfHeaderName = 'X-CSRFTOKEN'

export const ModificaDimensione = ({
  personalizzazione,
  personalizzazioni_righe_equivalenti,
  ordine,
}) => {
  const t = useTrans()
  const router = useRouter()
  const lang = router.query.lang

  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const [uploading, setUploading] = useState(false)

  const onDimensioneChange = async (values) => {
    setUploading(true)

    const body = {
      altezza: values.altezza,
      larghezza: values.larghezza,
    }

    try {
      const response = await api.put(
        `modifica-dimensione-personalizzazione-ordine/${personalizzazione.id}/`,
        body,
        {
          params: {
            lang,
            ordine_id: ordine.id,
            personalizzazioni_equivalenti_ids: personalizzazioni_righe_equivalenti
              .filter((pers) => pers.id !== personalizzazione.id)
              .map((pers) => pers.id),
          },
        }
      )
      const data = await response.data

      setAltezza(data.altezza)
      setLarghezza(data.larghezza)

      setErrorMessage('')
      setSuccessMessage('Dimensione modificata con successo')
    } catch (error) {
      setSuccessMessage('')
      setErrorMessage('Errore nel salvataggio della dimensione')
      Sentry.captureException(error)
    }
    setUploading(false)
  }

  const [altezza, setAltezza] = useState(personalizzazione.altezza)
  const [larghezza, setLarghezza] = useState(personalizzazione.larghezza)

  return (
    <div className="fp-edit-size">
      <p className="fp-edit-size__title">
        <Trans chiave="Dimensione (cm)" />
      </p>
      <p className="fp-edit-size__subtitle">
        <Trans chiave="E' possibile inserire il valore 0 per una delle due dimensioni per indicare che sarà in proporzione rispetto all'altra" />
      </p>
      {(errorMessage || successMessage) && (
        <div className="fp-edit-size__alert">
          {errorMessage && (
            <Alert placeholder={errorMessage} variant="error" icon={<AlertTriangleLine />} />
          )}
          {successMessage && <Alert placeholder={successMessage} variant="info" icon={<Info />} />}
        </div>
      )}
      <Formik
        initialValues={{
          altezza: altezza,
          larghezza: larghezza,
        }}
        enableReinitialize={true}
        onSubmit={(values, { resetForm }) => {
          onDimensioneChange(values).then(() => {
            resetForm({
              errors: {
                altezza: 'Required',
                larghezza: 'Required',
              },
            })
          })
        }}
        validateOnChange={true}
        validateOnMount={true}
        validate={(values) => {
          const errors = {}

          if (values.altezza < 0) {
            errors.altezza = 'Negative'
          }

          if (values.larghezza < 0) {
            errors.larghezza = 'Negative'
          }

          if (values.altezza === 0 && values.larghezza === 0) {
            errors.altezza = 'Required'
            errors.larghezza = 'Required'
          }

          if (values.altezza === altezza && values.larghezza === larghezza) {
            errors.altezza = 'Equal'
            errors.larghezza = 'Equal'
          }

          return errors
        }}
      >
        {({ values, handleChange, isValid }) => (
          <Form>
            <div className="fp-edit-size__fieldsets">
              <FormField label={t('Larghezza')}>
                <FormInput
                  type="number"
                  min={0}
                  id="larghezza"
                  name="larghezza"
                  value={values.larghezza}
                  onChange={handleChange}
                  placeholder={t('Inserisci larghezza')}
                />
              </FormField>
              <FormField label={t('Altezza')}>
                <FormInput
                  type="number"
                  min={0}
                  id="altezza"
                  name="altezza"
                  value={values.altezza}
                  onChange={handleChange}
                  placeholder={t('Inserisci altezza')}
                />
              </FormField>
            </div>
            <div>
              <Button
                type="submit"
                label={t('Salva')}
                variant="primary"
                size="large"
                disabled={!isValid || uploading}
              />
            </div>
          </Form>
        )}
      </Formik>
      <div className="fp-edit-size__notes">
        {altezza || larghezza ? (
          <p>
            <Trans chiave="Larghezza: " />
            {larghezza} cm, <Trans chiave="Altezza: " />
            {altezza} cm
          </p>
        ) : (
          <p>
            <Trans chiave="Dimensione: " />
            {personalizzazione.dimensione.nome}
          </p>
        )}
      </div>
    </div>
  )
}
